"use client"

import { useEffect, useRef } from "react"
import { gsap } from "gsap"
import { Link } from "react-router-dom"
import Footer from "./Footer"
import HomeNavbar from "./HomeNavbar"
import { FaChartLine, FaCopy, FaUsers, FaLock } from "react-icons/fa"

export default function HomePage() {
  const backgroundRef = useRef(null)
  const chartRef = useRef(null)
  const candlesRef = useRef(null)
  const heroTextRef = useRef(null)
  const featuresRef = useRef(null)
  const howItWorksRef = useRef(null)
  const brokersRef = useRef(null)

  const features = [
    { 
      icon: <FaChartLine />, 
      title: 'Detailed Trade Insights', 
      description: 'Access complete order logs, position details, and performance analytics across all your connected accounts in real-time.' 
    },
    { icon: <FaCopy />, title: 'Seamless Copying', description: 'Automatically replicate trades from successful traders in real-time' },
    { icon: <FaUsers />, title: 'Diverse Trader Network', description: 'Access a wide range of experienced traders across various markets' },
    { icon: <FaLock />, title: 'Secure Platform', description: 'Trade with confidence on our highly secure and regulated platform' },
  ];

  const steps = [
    { title: 'Create Your Account', description: 'Sign up for an account and complete the verification process to get started.' },
    { title: 'Select a Master Account', description: 'Choose a successful master trader whose strategies you want to copy.' },
    { title: 'Connect Child Accounts', description: 'Add your child accounts and link them to the master account for trade copying.' },
    { title: 'Configure Trading Parameters', description: 'Set trading parameters like multipliers and risk settings for your child accounts.' },
    { title: 'Start Copying Trades', description: 'Once set, watch as trades from the master account are automatically copied to your child accounts.' },
  ];

  useEffect(() => {
    const tl = gsap.timeline({ repeat: -1 })
    gsap.set(".chart-line", {
      stroke: "rgba(59, 130, 246, 0.2)",
      strokeDasharray: 1000,
      strokeDashoffset: 1000,
    })
    gsap.set(".candle", {
      scaleY: 0,
      transformOrigin: "bottom",
    })
    tl.to(".chart-line", {
      strokeDashoffset: 0,
      duration: 2,
      stagger: 0.1,
      ease: "power1.inOut",
    })
    tl.to(
      ".candle",
      {
        scaleY: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)",
      },
      "-=1.5",
    )
    gsap.to(".bg-pattern", {
      x: "10%",
      y: "5%",
      duration: 20,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    })

    const heroTextChildren = heroTextRef.current?.children || [];
    gsap.set(heroTextChildren, {
      y: 50,
      opacity: 0,
    })
    gsap.to(heroTextChildren, {
      y: 0,
      opacity: 1,
      duration: 1,
      stagger: 0.2,
      ease: "power3.out",
      delay: 0.5,
    })

    const featureCards = featuresRef.current?.querySelectorAll('.feature-card') || [];
    gsap.set(featureCards, { y: 50, opacity: 0 });
    const featureObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.1 * (Array.from(featureCards).indexOf(entry.target)),
            });
            featureObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    featureCards.forEach((card) => featureObserver.observe(card));

    const stepItems = howItWorksRef.current?.querySelectorAll('.step-item') || [];
    gsap.set(stepItems, { x: -50, opacity: 0 });
    const stepObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              x: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.1 * (Array.from(stepItems).indexOf(entry.target)),
            });
            stepObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    stepItems.forEach((item) => stepObserver.observe(item));

    const brokerLogos = brokersRef.current?.querySelectorAll('.broker-logo') || [];
    gsap.set(brokerLogos, { scale: 0.8, opacity: 0 });
    const brokerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(entry.target, {
              scale: 1,
              opacity: 1,
              duration: 0.8,
              ease: "power3.out",
              delay: 0.1 * (Array.from(brokerLogos).indexOf(entry.target)),
            });
            brokerObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    brokerLogos.forEach((logo) => brokerObserver.observe(logo));

    return () => {
      tl.kill();
      gsap.killTweensOf(".bg-pattern");
      gsap.killTweensOf(heroTextChildren);
      featureCards.forEach((card) => featureObserver.unobserve(card));
      stepItems.forEach((item) => stepObserver.unobserve(item));
      brokerLogos.forEach((logo) => brokerObserver.unobserve(logo));
    }
  }, []);

  const Button = ({ children, size, variant, className, to }) => {
    const baseClasses = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
    const sizeClasses = size === "lg" ? "h-11 px-8 py-3 text-base" : "h-10 px-4 py-2 text-sm"
    const variantClasses =
      variant === "outline"
        ? "border border-slate-600 text-white hover:bg-slate-800"
        : "bg-blue-600 hover:bg-blue-700 text-white"

    return to ? (
      <Link
        to={to}
        className={`${baseClasses} ${sizeClasses} ${variantClasses} ${className || ""}`}
      >
        {children}
      </Link>
    ) : (
      <button
        className={`${baseClasses} ${sizeClasses} ${variantClasses} ${className || ""}`}
      >
        {children}
      </button>
    )
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden">
      <HomeNavbar />

      <div ref={backgroundRef} className="fixed inset-0 opacity-20 pointer-events-none bg-pattern">
        <div ref={chartRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 1000" preserveAspectRatio="none">
            {[...Array(20)].map((_, i) => {
              const y1 = Number((800 - i * 30 + Math.sin(i) * 50).toFixed(2)) || 0
              const x2 = Number((250 + i * 20).toFixed(2)) || 0
              const y2 = Number((700 - Math.cos(i) * 100).toFixed(2)) || 0
              const x3 = Number((500 - i * 10).toFixed(2)) || 0
              const y3 = Number((600 + Math.sin(i) * 80).toFixed(2)) || 0
              const x4 = Number((750 + i * 5).toFixed(2)) || 0
              const y4 = Number((500 - Math.cos(i) * 60).toFixed(2)) || 0
              const y5 = Number((400 + Math.sin(i) * 100).toFixed(2)) || 0

              const coordinates = [y1, x2, y2, x3, y3, x4, y4, y5]
              if (!coordinates.every(coord => Number.isFinite(coord))) {
                console.error(`Invalid coordinates for path ${i}:`, { y1, x2, y2, x3, y3, x4, y4, y5 })
                return null
              }

              const d = `M 0 ${y1} C ${x2} ${y2} ${x3} ${y3} ${x4} ${y4} L 1000 ${y5}`

              return (
                <path
                  key={`line-${i}`}
                  className="chart-line"
                  d={d}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              )
            })}
          </svg>
        </div>
        <div ref={candlesRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 500">
            {[...Array(30)].map((_, i) => {
              const x = 100 + i * 30
              const height = 20 + Math.random() * 80
              const width = 8
              const isUp = Math.random() > 0.5
              const color = isUp ? "rgba(16, 185, 129, 0.7)" : "rgba(239, 68, 68, 0.7)"
              const wickHeight = height * 0.6
              return (
                <g key={`candle-${i}`}>
                  <line x1={x} y1={250 - wickHeight} x2={x} y2={250 + wickHeight} stroke={color} strokeWidth="1" />
                  <rect
                    className="candle"
                    x={x - width / 2}
                    y={isUp ? 250 - height : 250}
                    width={width}
                    height={height}
                    fill={color}
                    rx="1"
                  />
                </g>
              )
            })}
          </svg>
        </div>
      </div>

      <section className="relative z-10 pt-20 pb-32 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto min-h-[50vh]">
        <div ref={heroTextRef} className="max-w-3xl mx-auto text-center space-y-8 opacity-100">
          <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold tracking-tight text-white">
          Welcome to AlgoSoftware
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Sign up, select a master trader, connect your accounts, configure your settings, and start algo trades automatically.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
            <Button to="/pricing" size="lg">Start Free Trial</Button>
          </div>
        </div>
      </section>

      <section ref={featuresRef} className="relative z-10 py-24 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4"> Features</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Our platform combines advanced algorithms with an intuitive interface to give you the edge in today's
              markets.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card bg-slate-700/50 backdrop-blur-sm rounded-xl p-8 hover:bg-slate-700/80 transition-colors"
              >
                <div className="mb-4 h-8 w-8 text-blue-500">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-slate-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={howItWorksRef} className="relative z-10 py-20 px-4 md:px-6 lg:px-8 bg-slate-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">How Algosoftware Works</h2>
          <div className="max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="step-item flex items-start mb-8">
                <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg mr-4">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{step.title}</h3>
                  <p className="text-slate-300">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={brokersRef} className="relative z-10 py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Supported Brokers</h2>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            <div className="broker-logo w-32 h-16 flex items-center justify-center">
              <img src="/images/angelone.png" alt="Angel One Logo" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="broker-logo w-32 h-16 flex items-center justify-center">
              <img src="/images/upstox.jpg" alt="Upstox Logo" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="broker-logo w-32 h-16 flex items-center justify-center">
              <img src="/images/aliceblue.png" alt="Alice Blue Logo" className="max-w-full max-h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}